/**
 * Reglas de validación de números de pasaporte por país (ISO 3166-1 alpha-2).
 *
 * ⚠️ Alcance:
 * - Estas expresiones regulares VALIDAN ÚNICAMENTE EL FORMATO.
 * - No verifican autenticidad, vigencia ni existencia del pasaporte.
 *
 * 📚 Fuentes y criterios:
 * - Estándar ICAO Doc 9303 (Machine Readable Travel Documents).
 * - Documentación pública de autoridades migratorias nacionales.
 * - Ejemplos recopilados de bases abiertas(ej. DS-160 de EE. UU. o el sistema ETIAS de la UE) y validaciones de uso común.
 *
 * 📆 Última revisión: 2026-02
 *
 * 🔎 Notas de mantenimiento:
 * - Los formatos pueden cambiar por decisión soberana de cada país.
 * - Cualquier modificación debe incluir referencia a una fuente oficial.
 * - Usar siempre códigos de país ISO 3166-1 alpha-2 como clave.
 */


export const reglasPasaportesPorPais = {
    // Europa Occidental
    ES: /^([A-Z]{3}\d{6}|[A-Z]{2}\d{7})$/i, // España: AAA123456 o AA1234567
    FR: /^\d{2}[A-Z]{2}\d{5}$/i, // Francia: 12AB34567
    DE: /^[CFGHJKLMNPRTVWXYZ][0-9CFGHJKLMNPRTVWXYZ]{8}$/i, // Alemania: C01X00T47
    IT: /^[A-Z]{2}\d{7}$/i, // Italia: AA1234567
    PT: /^[A-Z]{1,2}\d{6}$/i, // Portugal: N123456 o AB123456
    GB: /^(\d{9}|[A-Z]{2}\d{7})$/i, // Reino Unido: 123456789 o GB1234567
    IE: /^[A-Z]{2}\d{7}$/i, // Irlanda: PM1234567
    NL: /^[A-Z]{2}[A-Z0-9]{6}\d$/i, // Países Bajos: NL12AB5CD
    BE: /^[A-Z]{2}\d{6}$/i, // Bélgica: EH123456
    LU: /^[A-Z0-9]{8}$/i, // Luxemburgo: 12345678
    CH: /^[A-Z]\d{7}$/i, // Suiza: C1234567
    AT: /^[A-Z]\d{7}$/i, // Austria: P1234567
    MC: /^\d{8}[A-Z]$/i, // Mónaco: 12345678A
    AD: /^[A-Z]\d{6}$/i, // Andorra: L123456
    LI: /^[A-Z]\d{5}$/i, // Liechtenstein: P12345
    SM: /^[A-Z]{2}\d{6}$/i, // San Marino: SM123456

    // Europa del Norte
    SE: /^\d{8}$/, // Suecia: 12345678
    NO: /^([A-Z]\d{8}|[A-Z]{2}\d{6})$/i, //  Noruega: A12345678 (Nuevo) o AB123456 (Antiguo)
    DK: /^\d{9}$/, // Dinamarca: 123456789
    FI: /^[A-Z]{2}\d{7}$/i, // Finlandia: PE1234567
    IS: /^[A-Z]\d{7}$/i, // Islandia: A1234567

    // Europa del Este
    PL: /^[A-Z]{2}\d{7}$/i, // Polonia: AA1234567
    CZ: /^\d{8}$/, // República Checa: 12345678
    SK: /^[A-Z]{2}\d{7}$/i, // Eslovaquia: AA1234567
    HU: /^[A-Z]{2}\d{6,7}$/i, // Hungría: AA123456
    RO: /^\d{8,9}$/, // Rumanía: 12345678
    BG: /^\d{9}$/, // Bulgaria: 123456789
    SI: /^[A-Z]{2}\d{6}$/i, // Eslovenia: P0123456
    HR: /^\d{9}$/, // Croacia: 123456789
    BA: /^[A-Z]\d{7}$/i, // Bosnia: A1234567
    RS: /^\d{9}$/, // Serbia: 123456789
    MK: /^[A-Z]\d{7}$/i, // Macedonia: A1234567
    AL: /^[A-Z]\d{7}$/i, // Albania: A1234567
    EE: /^[A-Z]\d{7}$/i, // Estonia: N1234567
    LV: /^[A-Z]{2}\d{7}$/i, // Letonia: LV1234567
    LT: /^\d{8}$/, // Lituania: 12345678
    UA: /^[A-Z]{2}\d{6}$/i, // Ucrania: AA123456
    BY: /^[A-Z]{2}\d{7}$/i, // Bielorrusia: AB1234567
    MD: /^\d{9}$/, // Moldavia: 123456789
    RU: /^\d{9}$/, // Rusia: 123456789
    GE: /^\d{2}[A-Z]{2}\d{5}$/i, // Georgia: 2 dígitos, 2 letras y 5 dígitos
    AM: /^[A-Z]{2}\d{7}$/i, // Armenia: AM1234567
    AZ: /^[A-Z]{2}\d{7}$/i, // Azerbaiyán: AZ1234567

    // América del Norte
    US: /^(\d{9}|[A-Z]\d{8})$/i, // USA (nuevo): 123456789 | (antiguo): A12345678
    CA: /^[A-Z]{2}\d{6}$/i, // Canadá: AB123456
    MX: /^[A-Z]\d{8}$/i, // México: G12345678

    // América Central y Caribe
    GT: /^[A-Z]\d{8}$/i, // Guatemala: A12345678
    HN: /^[A-Z]\d{7}$/i, // Honduras: A1234567
    SV: /^[A-Z]\d{8}$/i, // El Salvador: A12345678
    NI: /^[A-Z]\d{7}$/i, // Nicaragua: C1234567
    CR: /^\d{9}$/, // Costa Rica: 123456789
    PA: /^[A-Z]\d{8}$/i, // Panamá: N12345678
    BZ: /^[A-Z]\d{5}$/i, // Belice: A12345
    CU: /^[A-Z]\d{7}$/i, // Cuba: A1234567
    DO: /^[A-Z]\d{8}$/i, // República Dominicana: A12345678
    HT: /^[A-Z]{2}\d{6}$/i, // Haití: AD123456
    JM: /^[A-Z]\d{7}$/i, // Jamaica: A1234567
    TT: /^[A-Z]{2}\d{6}$/i, // Trinidad y Tobago: TT123456
    BB: /^[A-Z]{2}\d{6}$/i, // Barbados: AN123456
    BS: /^[A-Z]\d{8}$/i, // Bahamas: A12345678
    LC: /^[A-Z]\d{7}$/i, // Santa Lucía: A1234567
    VC: /^[A-Z]\d{7}$/i, // San Vicente: A1234567
    GD: /^[A-Z]\d{7}$/i, // Granada: C1234567
    AG: /^[A-Z]\d{7}$/i, // Antigua y Barbuda: A1234567
    KN: /^[A-Z]\d{7}$/i, // San Cristóbal: N1234567
    PR: /^\d{9}$/, // Puerto Rico: 123456789

    // América del Sur
    AR: /^[A-Z]{3}\d{6}$/i, // Argentina: AAA123456
    BR: /^[A-Z]{2}\d{6}$/i, // Brasil: AA123456
    CO: /^[A-Z]{2}\d{6}$/i, // Colombia: AA123456
    CL: /^\d{8,9}$/, // Chile: 12345678
    PE: /^\d{9}$/, // Perú: 123456789
    VE: /^[A-Z]\d{8}$/i, // Venezuela: E12345678
    EC: /^[A-Z]{3}\d{6}$/i, // Ecuador: AAA123456
    UY: /^[A-Z]{3}\d{5}$/i, // Uruguay: AAA12345
    PY: /^[A-Z]\d{7}$/i, // Paraguay: A1234567
    BO: /^[A-Z]\d{8}$/i, // Bolivia: A12345678
    SR: /^[A-Z]{2}\d{6}$/i, // Surinam: AB123456
    GY: /^[A-Z]\d{6}$/i, // Guyana: A123456

    // Asia Oriental
    CN: /^[EG]\d{8}$/i, // China: E12345678 o G12345678
    JP: /^[A-Z]{2}\d{7}$/i, // Japón: TH1234567
    KR: /^[A-Z]\d{8}$/i, // Corea del Sur: M12345678
    KP: /^[A-Z]{2}\d{9}$/i, // Corea del Norte: AB123456789
    TW: /^\d{9}$/, // Taiwán: 123456789
    HK: /^[A-Z]\d{7,8}$/i, // Hong Kong: K1234567
    MO: /^\d{7,8}$/, // Macao: 1234567
    MN: /^[A-Z]\d{7}$/i, // Mongolia: A1234567

    // Sudeste Asiático
    TH: /^[A-Z]{2}\d{6}$/i, // Tailandia: AA123456
    VN: /^[A-Z]\d{7,8}$/i, // Vietnam: B1234567
    PH: /^[A-Z]{2}\d{7}$/i, // Filipinas: P1234567A
    ID: /^[A-Z]\d{7}$/i, // Indonesia: A1234567
    MY: /^[A-Z]\d{8}$/i, // Malasia: A12345678
    SG: /^[A-Z]\d{7}[A-Z]$/i, // Singapur: K1234567G
    BN: /^[A-Z]\d{6}$/i, // Brunéi: B123456
    MM: /^[A-Z]\d{6}$/i, // Myanmar: MA123456
    KH: /^[A-Z]\d{8}$/i, // Camboya: N12345678
    LA: /^[A-Z]\d{7}$/i, // Laos: P1234567
    TL: /^\d{7}$/, // Timor Oriental: 1234567

    // Asia del Sur
    IN: /^[A-Z]\d{7}$/i, // India: A1234567
    PK: /^[A-Z]{2}\d{7}$/i, // Pakistán: AB1234567
    BD: /^[A-Z]{2}\d{7}$/i, // Bangladesh: AB1234567
    LK: /^[A-Z]\d{7}$/i, // Sri Lanka: N1234567
    NP: /^\d{7,8}$/, // Nepal: 12345678
    BT: /^[A-Z]\d{7}$/i, // Bután: A1234567
    MV: /^[A-Z]\d{6,7}$/i, // Maldivas: A123456
    AF: /^[A-Z]{2}\d{7}$/i, // Afganistán: ON1234567

    // Asia Occidental (Medio Oriente)
    TR: /^[A-Z]\d{8}$/i, // Turquía: M12345678
    SA: /^[A-Z]\d{8}$/i, // Arabia Saudita: A12345678
    AE: /^[A-Z]\d{7}$/i, // Emiratos Árabes: A1234567
    IL: /^\d{7,9}$/, // Israel: 12345678
    IQ: /^[A-Z]\d{8}$/i, // Irak: A12345678
    IR: /^[A-Z]\d{8}$/i, // Irán: A12345678
    JO: /^[A-Z]\d{7,8}$/i, // Jordania: A1234567
    LB: /^\d{6,9}$/, // Líbano: 123456
    SY: /^\d{9,11}$/, // Siria: 12345678901
    KW: /^\d{9}$/, // Kuwait: 123456789
    QA: /^\d{8}$/, // Qatar: 12345678
    BH: /^\d{8,9}$/, // Baréin: 12345678
    OM: /^\d{8}$/, // Omán: 12345678
    YE: /^\d{8}$/, // Yemen: 12345678

    // Asia Central
    KZ: /^[A-Z]\d{8}$/i, // Kazajistán: N12345678
    UZ: /^[A-Z]{2}\d{7}$/i, // Uzbekistán: AB1234567
    TM: /^[A-Z]\d{7}$/i, // Turkmenistán: A1234567
    KG: /^[A-Z]{2}\d{7}$/i, // Kirguistán: AN1234567
    TJ: /^\d{9}$/, // Tayikistán: 123456789

    // Oceanía
    AU: /^[A-Z]\d{7}$/i, // Australia: N1234567
    NZ: /^[A-Z]{2}\d{6}$/i, // Nueva Zelanda: LA123456
    PG: /^[A-Z]\d{7}$/i, // Papúa Nueva Guinea: N1234567
    FJ: /^[A-Z]\d{7}$/i, // Fiyi: A1234567
    WS: /^[A-Z]\d{6}$/i, // Samoa: A123456
    TO: /^[A-Z]\d{5}$/i, // Tonga: A12345
    VU: /^\d{8}$/, // Vanuatu: 12345678
    SB: /^[A-Z]\d{6}$/i, // Islas Salomón: A123456
    KI: /^[A-Z]\d{8}$/i, // Kiribati: P12345678
    TV: /^[A-Z]\d{7}$/i, // Tuvalu: A1234567
    NR: /^[A-Z]\d{7}$/i, // Nauru: A1234567
    PF: /^\d{7}$/, // Polinesia Francesa: 1234567
    NC: /^\d{7}$/, // Nueva Caledonia: 1234567

    // África del Norte
    EG: /^[A-Z]\d{7,8}$/i, // Egipto: A1234567
    MA: /^[A-Z]{2}\d{6}$/i, // Marruecos: AA123456
    DZ: /^\d{9}$/, // Argelia: 123456789
    TN: /^[A-Z]\d{7}$/i, // Túnez: A1234567
    LY: /^\d{12}$/, // Libia: 123456789012
    SD: /^[A-Z]\d{7}$/i, // Sudán: A1234567

    // África Occidental
    NG: /^[A-Z]\d{8}$/i, // Nigeria: A12345678
    GH: /^[A-Z]\d{8}$/i, // Ghana: G12345678
    SN: /^\d{12}$/, // Senegal: 123456789012
    CI: /^\d{2}[A-Z]{2}\d{5}$/i, // Costa de Marfil: 12AB12345
    ML: /^[A-Z]{2}\d{6}$/i, // Malí: AM123456
    BF: /^[A-Z]\d{7}$/i, // Burkina Faso: A1234567
    NE: /^\d{8}$/, // Níger: 12345678
    TG: /^[A-Z]\d{7}$/i, // Togo: A1234567
    BJ: /^\d{9}$/, // Benín: 123456789
    GM: /^[A-Z]\d{7}$/i, // Gambia: A1234567
    GW: /^[A-Z]\d{7}$/i, // Guinea-Bissau: A1234567
    GN: /^\d{9}$/, // Guinea: 123456789
    SL: /^[A-Z]\d{7}$/i, // Sierra Leona: P1234567
    LR: /^[A-Z]{2}\d{7}$/i, // Liberia: AB1234567
    CV: /^\d{7}$/, // Cabo Verde: 1234567

    // África Central
    CM: /^[A-Z]\d{8}$/i, // Camerún: A12345678
    CG: /^\d{9}$/, // Congo Brazzaville: 123456789
    CD: /^[A-Z]{2}\d{7}$/i, // Congo Kinshasa: OC1234567
    CF: /^\d{9}$/, // República Centroafricana: 123456789
    TD: /^\d{9}$/, // Chad: 123456789
    GA: /^\d{8}$/, // Gabón: 12345678
    GQ: /^[A-Z]\d{8}$/i, // Guinea Ecuatorial: N12345678
    ST: /^[A-Z]\d{7}$/i, // Santo Tomé: A1234567

    // África Oriental
    KE: /^[A-Z]\d{7,8}$/i, // Kenia: A1234567
    TZ: /^[A-Z]{2}\d{7}$/i, // Tanzania: AB1234567
    UG: /^[A-Z]\d{7}$/i, // Uganda: A1234567
    RW: /^\d{8}$/, // Ruanda: 12345678
    BI: /^[A-Z]{2}\d{6}$/i, // Burundi: AB123456
    ET: /^[A-Z]{2}\d{7}$/i, // Etiopía: EP1234567
    SO: /^[A-Z]\d{7}$/i, // Somalia: A1234567
    DJ: /^\d{12}$/, // Yibuti: 123456789012
    ER: /^\d{9}$/, // Eritrea: 123456789
    SC: /^[A-Z]\d{6}$/i, // Seychelles: C123456
    MU: /^[A-Z]\d{6}$/i, // Mauricio: A123456
    KM: /^\d{8}$/, // Comoras: 12345678
    MG: /^\d{8}$/, // Madagascar: 12345678

    // África Austral
    ZA: /^[A-Z]\d{8}$/i, // Sudáfrica: A12345678
    ZW: /^[A-Z]{2}\d{6}$/i, // Zimbabue: AN123456
    ZM: /^[A-Z]{2}\d{6}$/i, // Zambia: ZN123456
    BW: /^\d{9}$/, // Botsuana: 123456789
    NA: /^\d{9}$/, // Namibia: 123456789
    MZ: /^\d{9}$/, // Mozambique: 123456789
    MW: /^[A-Z]{2}\d{7}$/i, // Malaui: MN1234567
    LS: /^[A-Z]\d{8}$/i, // Lesoto: A12345678
    SZ: /^[A-Z]\d{7}$/i, // Esuatini: A1234567
    AO: /^\d{9}$/, // Angola: 123456789
    MR: /^\d{9}$/, // Mauritania: 123456789

    // Territorios Especiales
    CY: /^[A-Z]\d{7,8}$/i, // Chipre: K00123456
    MT: /^\d{7}$/, // Malta: 1234567
    GI: /^\d{6}$/, // Gibraltar: 123456
    IM: /^\d{9}$/, // Isla de Man: 123456789
    JE: /^[A-Z]\d{7}$/i, // Jersey: J123456
    GG: /^\d{9}$/, // Guernsey: 123456789
    FO: /^\d{7}$/, // Islas Feroe: 1234567
    GL: /^\d{9}$/, // Groenlandia: 123456789
    AW: /^[A-Z]\d{6}$/i, // Aruba: V123456
    BM: /^\d{9}$/, // Bermudas: 123456789
    VG: /^[A-Z]\d{6,7}$/i, // Islas Vírgenes Británicas: V123456
    TC: /^[A-Z]\d{6}$/i, // Islas Turcas y Caicos: T123456
    AI: /^\d{9}$/, // Anguila: 123456789
    RE: /^\d{9}$/, // Reunión: 123456789
    MQ: /^\d{9}$/, // Martinica: 123456789
    PM: /^\d{9}$/, // San Pedro y Miquelón: 123456789
    EH: /^\d{8}$/, // Sáhara Occidental: 12345678
};
